import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@pondigitalsolutions/pon-component-library";

import { languageEnglish } from "../i18n";
import { useUserContext } from "@hooks/useUserContext";
import { useGetWalkthrough } from "@hooks/useGetWalkthrough";
import { useUpdateWalkthroughsFinished } from "@hooks/useUpdateWalkthroughsFinished";
import { WalkthroughStory } from "../types/WalkthroughTypes";
import { useCurrentLang } from "@hooks/useCurrentLang";
import { LanguageDropdown } from "./LanguageDropdown";

interface WalkthroughProps {
  translatedSlug: string;
}

export const Walkthrough = ({ translatedSlug }: WalkthroughProps) => {
  const { t } = useTranslation();
  const { finishedWalkthroughs } = useUserContext();
  const { mutate: updateWalkthroughsFinished } =
    useUpdateWalkthroughsFinished();
  const currentLang = useCurrentLang();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Check if any of the walkthroughs is onboarding.
  const isOnboardingFinished = finishedWalkthroughs.some(
    (walkthrough) => walkthrough.isOnboarding
  );

  const { data, isLoading } = useGetWalkthrough({
    content_type: "walkthrough",
    // No need to fetch if onboarding is finished.
    abortRequest: isOnboardingFinished ? true : undefined,
    language: currentLang === languageEnglish ? languageEnglish : undefined,
    filter_query: {
      isOnboarding: {
        in: !isOnboardingFinished,
      },
    },
  });

  const [walkthrough, setWalkthrough] = useState<WalkthroughStory | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [walkthroughFinished, setWalkthroughFinished] = useState(false);

  useEffect(() => {
    // Check if onboarding is already finished
    if (isOnboardingFinished) return;

    const slides = data?.storyData?.[0]?.content?.slides;

    // If still loading or no content, do nothing
    if (isLoading || !slides || slides.length === 0) return;
    if (walkthroughFinished) return;

    setWalkthrough(data?.storyData[0] ?? null);

    // Show the dialog using show() instead of showModal() to avoid top layer
    // This allows z-index to work properly so Cookiebot banner can appear above
    dialogRef.current?.show();
    setIsOpen(true);

    // Focus the first focusable element in the dialog
    const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusableElements && focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }, [isOnboardingFinished, isLoading, data, walkthroughFinished]);

  // Handle keyboard events for focus trap and escape prevention
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent Escape key from closing the dialog
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Focus trap: keep Tab navigation within the dialog
      if (e.key === "Tab" && dialogRef.current) {
        const focusableElements =
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          // Shift+Tab on first element: move to last
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          // Tab on last element: move to first
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen]);

  const isLastSlide =
    walkthrough && activeIndex === walkthrough.content.slides.length - 1;
  const isFirstSlide = activeIndex === 0;

  const handleNextButton = () => {
    if (isLastSlide && walkthrough.id) {
      updateWalkthroughsFinished({
        walkthroughId: walkthrough.id,
        isOnboarding: walkthrough.content.isOnboarding,
      });
      setWalkthroughFinished(true);
      dialogRef.current?.close();
      setIsOpen(false);
    } else {
      setActiveIndex((prev) => prev + 1);
    }
  };

  if (!walkthrough) {
    return null;
  }

  return (
    <>
      {/* Custom backdrop since show() doesn't create one */}
      {isOpen && (
        <div className="fixed inset-0 z-[999] bg-black/50" aria-hidden="true" />
      )}
      <dialog
        ref={dialogRef}
        className="fixed inset-0 z-[999] m-auto w-full overflow-hidden rounded-xl lg:w-164 dark:bg-tertiary-900"
        aria-modal="true"
        aria-labelledby="walkthrough-dialog-title"
      >
        {walkthrough.content.slides.map((step, index) => (
          <div
            key={step.title}
            className={index === activeIndex ? "" : "hidden"}
          >
            <img
              src={step.image.filename}
              alt={step.image.alt || step.title}
              className="aspect-video w-full"
            />
          </div>
        ))}
        {/* Progress bar - outside map so it can animate */}
        <progress
          className="block h-2 w-full appearance-none bg-tertiary-100 [&::-moz-progress-bar]:bg-primary-500 [&::-moz-progress-bar]:transition-all [&::-moz-progress-bar]:duration-300 [&::-moz-progress-bar]:ease-out [&::-webkit-progress-bar]:bg-tertiary-100 [&::-webkit-progress-value]:bg-primary-500 [&::-webkit-progress-value]:transition-all [&::-webkit-progress-value]:duration-300 [&::-webkit-progress-value]:ease-out"
          value={activeIndex + 1}
          max={walkthrough.content.slides.length}
          aria-label={t("walkthrough.walkthroughProgress")}
        />
        {/* Content */}
        {walkthrough.content.slides.map((step, index) => (
          <div
            key={step.title}
            className={`flex flex-col gap-2 p-6 pb-2 lg:gap-4 lg:p-9 lg:pb-4 ${index === activeIndex ? "" : "hidden"}`}
          >
            <Typography
              as="h2"
              id={
                index === activeIndex ? "walkthrough-dialog-title" : undefined
              }
              size="lg"
              className="leading-5.5 font-bold text-secondary-700 lg:text-2xl dark:text-white"
            >
              {step.title}
            </Typography>
            <Typography
              as="p"
              className="leading-5.5 font-normal text-tertiary-900 dark:text-tertiary-100"
            >
              {step.description}
            </Typography>
          </div>
        ))}
        <div className="flex justify-between px-6 py-4">
          {!isFirstSlide && (
            <Button
              id="previous-button"
              variant="secondary"
              onClick={() => setActiveIndex((prev) => prev - 1)}
              size="xl"
              startIcon="ChevronLeft"
              ariaLabel={t("walkthrough.previous")}
              className="aspect-square rounded-full p-0"
              disabled={activeIndex === 0}
            />
          )}
          {/* Only show language dropdown on first slide of the onboarding */}
          {isFirstSlide && !isOnboardingFinished && (
            <LanguageDropdown translatedSlug={translatedSlug} />
          )}
          <Button
            id="continue-button"
            variant="primary"
            onClick={handleNextButton}
            size="xl"
            endIcon="ChevronRight"
            ariaLabel={
              isLastSlide ? t("walkthrough.finish") : t("walkthrough.continue")
            }
            className={isFirstSlide ? "ml-auto" : ""}
          >
            {isLastSlide ? t("walkthrough.finish") : t("walkthrough.continue")}
          </Button>
        </div>
      </dialog>
    </>
  );
};
